import * as React from "react";

import { Card, Skeleton } from "@/components/ui";
import { cn } from "@/lib/utils";

import { VideoProjectKanbanCardSkeletonLayout } from "./types";

const COLUMN_SKELETON_COUNT = 3;

const PLAYER_LAYOUTS = [
  { title: "w-[72%]", date: "w-20" },
  { title: "w-[58%]", date: "w-16" },
  { title: "w-[80%]", date: "w-24" },
] as const;

const STAFF_LAYOUTS = [
  { title: "w-[72%]", meta: "w-16", date: "w-20" },
  { title: "w-[58%]", meta: "w-24", date: "w-16" },
  { title: "w-[80%]", meta: "w-14", date: "w-24" },
] as const;

export function VideoProjectKanbanCardSkeleton({
  layout,
  variant = 0,
  className,
}: {
  layout: VideoProjectKanbanCardSkeletonLayout;
  variant?: number;
  className?: string;
}) {
  if (layout === "staff") {
    const staffLayout = STAFF_LAYOUTS[variant % STAFF_LAYOUTS.length];
    return (
      <Card
        size="sm"
        aria-hidden="true"
        className={cn("pointer-events-none gap-0 overflow-hidden data-[size=sm]:py-0 ring-foreground/10", className)}
      >
        <div
          className="flex flex-col gap-2 p-2.5 [&_[data-slot=skeleton]]:[animation-delay:var(--skeleton-delay)]"
          style={{ "--skeleton-delay": `${variant * 140}ms` } as React.CSSProperties}
        >
          <div className="flex items-center justify-between gap-2">
            <Skeleton className="h-3 w-12" />
            <div className="flex items-center gap-1.5">
              <Skeleton className="size-6 rounded-full" />
              <Skeleton className="size-6 rounded-full" />
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <Skeleton className="size-16 shrink-0 rounded-md" />
            <div className="flex min-w-0 flex-1 flex-col gap-1.5 pt-0.5">
              <Skeleton className={cn("h-4", staffLayout.title)} />
              <Skeleton className={cn("h-3", staffLayout.meta)} />
              <Skeleton className="h-5 w-20 rounded-full" />
            </div>
          </div>
          <Skeleton className={cn("h-3", staffLayout.date)} />
        </div>
      </Card>
    );
  }

  const playerLayout = PLAYER_LAYOUTS[variant % PLAYER_LAYOUTS.length];
  return (
    <Card
      size="sm"
      aria-hidden="true"
      className="pointer-events-none gap-0 overflow-hidden data-[size=sm]:py-0 ring-foreground/10"
    >
      <div className="flex flex-col gap-2 p-2.5">
        <div className="flex items-start gap-2.5">
          <Skeleton className="size-16 shrink-0 rounded-md" />
          <div className="flex min-w-0 flex-1 flex-col gap-1 pt-0.5">
            <div className="flex items-start gap-1.5">
              <Skeleton className="size-6 shrink-0 rounded-full" />
              <Skeleton className={cn("mt-1 h-4", playerLayout.title)} />
            </div>
            <Skeleton className={cn("h-3", playerLayout.date)} />
          </div>
        </div>
      </div>
    </Card>
  );
}

export function VideoProjectKanbanColumnSkeletons({ layout }: { layout: VideoProjectKanbanCardSkeletonLayout }) {
  const skeletons = Array.from({ length: COLUMN_SKELETON_COUNT }, (_, index) => (
    <VideoProjectKanbanCardSkeleton
      key={index}
      layout={layout}
      variant={index}
      className={layout === "staff" ? (index === 1 ? "opacity-75" : index === 2 ? "opacity-40" : undefined) : undefined}
    />
  ));

  if (layout === "staff") {
    return (
      <div className="flex flex-col gap-2 animate-in fade-in duration-200" aria-hidden="true">
        {skeletons}
      </div>
    );
  }

  return <>{skeletons}</>;
}
